import { useEffect, useSyncExternalStore } from "react";

/**
 * Whether a full-screen editor is covering the app shell.
 *
 * The flow editor renders as a `fixed inset-0` overlay from inside its page,
 * so nothing in the URL says it is open. The floating Guía button sits above
 * it at a higher z-index and lands on the editor's own controls — on the
 * simulator it covers the message box. Overlays register here while mounted so
 * the shell's floating chrome can step aside.
 */
let depth = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Call on mount; the returned function releases on unmount. */
export function pushFullscreenOverlay(): () => void {
  depth += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    depth = Math.max(0, depth - 1);
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function isFullscreenOverlayOpen(): boolean {
  return depth > 0;
}

export function useFullscreenOverlayOpen(): boolean {
  return useSyncExternalStore(subscribe, isFullscreenOverlayOpen, () => false);
}

/** Registers an overlay for as long as `active` holds. For overlays that stay
 *  mounted while closed, like a Sheet: a modal one makes the rest of the page
 *  inert, so floating chrome left above it only covers its content. */
export function useFullscreenOverlay(active: boolean): void {
  useEffect(() => (active ? pushFullscreenOverlay() : undefined), [active]);
}
